// SEO landing page for an individual tool category.
//
// The in-app catalog (/tools) drives category clicks through this
// path-based route:
//
//   /tools/category/<slug>
//
// is statically pre-rendered for every enabled category, with its own
// title/description/canonical/JSON-LD, and re-uses the existing
// `ToolsPage` (filtered by category) so the in-app behaviour is
// identical. The query-string form (`/tools?category=<slug>`) keeps
// working unchanged.
//
// Mirrors the SEO pattern used by /tools/[slug], /tasks/category/[slug],
// /agents/category/[slug], and /explore/category/[slug].

import React, { useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import { toolApi as toolApiSsr } from '@/lib/toolApi';
import { ToolsPage } from '@/app/(app)/tools/index';

// ---------------------------------------------------------------------------
// SEO — static generation
// ---------------------------------------------------------------------------
// Module-level cache populated by `generateStaticParams` so the build-time
// HTML render has access to the same data the runtime render will. Keyed by
// slug; each entry holds `{ category, tools }`.
const _staticCategoryCache = {};

export async function generateStaticParams() {
  // Rendered on demand (client SPA), not prerendered. Returning [] keeps
  // `expo export` fast and avoids hammering the API at build time.
  return [];
}

// ---------------------------------------------------------------------------
// MetaHead helpers
// ---------------------------------------------------------------------------
const SITE_URL = 'https://gab.ai';

const titleCase = (s) =>
  String(s || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(p => p[0].toUpperCase() + p.slice(1))
    .join(' ');

const buildCategoryTitle = (cat, slug) => {
  if (cat?.meta?.title) return cat.meta.title;
  const name = cat?.name || titleCase(slug);
  if (name) return `${name} AI Tools — Gab AI`;
  return 'AI Tools';
};

const buildCategoryDescription = (cat, slug) => {
  if (cat?.meta?.description) return cat.meta.description;
  if (cat?.description) return cat.description;
  const name = (cat?.name || titleCase(slug) || '').toLowerCase();
  if (name) {
    return `Browse specialized ${name} AI tools on Gab AI. Fill out a form, get a result, then continue in chat.`;
  }
  return 'Browse Gab AI’s catalog of specialized AI tools. Fill out a form, get a result, then continue in chat.';
};

const buildKeywords = (cat, slug) => {
  if (cat?.meta?.keywords) return cat.meta.keywords;
  const name = cat?.name || titleCase(slug);
  const base = ['AI tools', 'AI utilities', 'AI generators', 'Gab AI tools'];
  if (name) base.unshift(name, `${name} AI`, `AI ${name}`);
  return base.join(', ');
};

const buildStructuredData = (cat, tools, slug) => {
  const url = `${SITE_URL}/tools/category/${slug}`;
  const name = cat?.name || titleCase(slug);
  const itemList = (tools || []).slice(0, 30).map((tool, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    url: tool?.slug ? `${SITE_URL}/tools/${tool.slug}` : url,
    name: tool?.meta?.title || tool?.name || tool?.slug || `Tool ${idx + 1}`,
  }));

  const collection = {
    '@type': 'CollectionPage',
    name: buildCategoryTitle(cat, slug),
    description: buildCategoryDescription(cat, slug),
    url,
    isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
    inLanguage: 'en-US',
    ...(itemList.length
      ? { mainEntity: { '@type': 'ItemList', itemListElement: itemList, numberOfItems: itemList.length } }
      : {}),
  };

  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'AI Tools', item: `${SITE_URL}/tools` },
      { '@type': 'ListItem', position: 3, name: name || slug, item: url },
    ],
  };

  return { '@context': 'https://schema.org', '@graph': [collection, breadcrumbs] };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ToolsCategoryRoute() {
  const params = useLocalSearchParams();
  const slug =
    typeof params?.slug === 'string'
      ? params.slug
      : Array.isArray(params?.slug)
        ? params.slug[0]
        : null;

  const cached = useMemo(() => {
    if (!slug) return null;
    return _staticCategoryCache[slug] || null;
  }, [slug]);

  const seo = useMemo(() => {
    if (!slug) {
      return {
        title: 'AI Tools — Gab AI',
        description: 'Browse Gab AI’s catalog of specialized AI tools.',
        canonical: '/tools',
        keywords: 'AI tools, AI utilities, AI generators, Gab AI tools',
        structuredData: null,
      };
    }
    const category = cached?.category || null;
    const tools = cached?.tools || [];
    return {
      title: buildCategoryTitle(category, slug),
      description: buildCategoryDescription(category, slug),
      canonical: `/tools/category/${slug}`,
      keywords: buildKeywords(category, slug),
      structuredData: buildStructuredData(category, tools, slug),
    };
  }, [cached, slug]);

  return (
    <>
      <MetaHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        keywords={seo.keywords}
        structuredData={seo.structuredData}
      />
      <ToolsPage categorySlug={slug || undefined} />
    </>
  );
}
